"use client";

import React, { useState } from "react";
import { cn } from "@/lib/utils";
import Container from "../Container";

type Tile = {
  type: "student" | "org";
  name: string;
  quote: string;
  klas?: string;
  avatar?: string;
  logo?: string;
  subtitle?: string;
  noQuotes?: boolean;
  logoClass?: string;
  link?: string;
  linkLabel?: string;
};

const ORG_TILES: Tile[] = [
  {
    type: "org",
    name: "Het Financieele Dagblad",
    logo: "/fd.png",
    link: "https://fd.nl/tech-en-innovatie/1588534/als-we-ook-menselijke-druk-in-de-app-kunnen-zetten-dan-zijn-we-klaar",
    linkLabel: "Lees het artikel in Het FD",
    quote:
      "WisWiz, de wiskunde wizard: een bijles-app die aansluit op Getal & Ruimte en Moderne Wiskunde."
  },
  {
    type: "org",
    name: "Het Parool",
    logo: "/Parool.png",
    link: "https://www.parool.nl/ps/bijles-in-je-broekzak-philip-20-en-stijn-21-ontwikkelden-een-wiskunde-app-voor-scholieren~b0f76f47/",
    linkLabel: "Lees het artikel in Het Parool",
    quote:
      "In onze stappenplannen wordt namelijk niet alleen de vraag uitgewerkt, maar wordt de oplossing eerst opgedeeld in blokjes..."
  },
  {
    type: "org",
    name: "Stichting Wiskunde Actief",
    logo: "/wiskundeactief.png",
    logoClass: "grayscale brightness-0 contrast-125",
    quote:
      "WisWiz is een krachtige tool waarin verschillende aspecten van AI gecombineerd worden voor een persoonlijke leerervaring."
  },
  {
    type: "org",
    name: "Vossius Gymnasium",
    logo: "/vossius4.png",
    noQuotes: true,
    quote:
      "Op het Vossius Gymnasium gebruiken leerlingen WisWiz voor persoonlijke uitleg buiten de reguliere lessen."
  },
  {
    type: "org",
    name: "Stichting Wijs Bijles",
    logo: "/wijs-bijles.png",
    noQuotes: true,
    quote:
      "De bijlesleerlingen van Stichting Wijs Bijles hebben toegang tot WisWiz om ook buiten de bijles hulp te krijgen bij wiskunde."
  }
];

const STUDENT_TILES: Tile[] = [
  {
    type: "student",
    name: "Valentijn",
    klas: "6e klas VWO",
    avatar: "/avatar-valentijn.png",
    quote:
      "Ben erg tevreden met jullie website en het heeft me ook zeker geholpen met m'n cijfers, en het maakt wiskunde een heel stuk makkelijker, dus heel erg bedankt daarvoor!"
  },
  {
    type: "student",
    name: "Mattia",
    klas: "VWO · inmiddels geslaagd",
    avatar: "/avatar-mattia.png",
    quote:
      "Ik zit zelf op 6 gymnasium op het Vossius en vroeg me af wanneer jullie H15 zullen vrijgeven, ik heb namelijk echt heel veel aan WisWiz gehad voor hoofdstuk 14."
  },
  {
    type: "student",
    name: "Valerie",
    klas: "VWO · inmiddels geslaagd",
    avatar: "/valerie.png",
    quote: "Heyy, ik ben bezig met h9 en het helpt erg goed."
  },
  {
    type: "student",
    name: "L. Reek, dochter van Ceres",
    subtitle: "Leven met Pubers",
    avatar: "/levenmetpubers-round.png",
    quote:
      "Ik gebruik WisWiz nu voor wiskunde en eerlijk gezegd vind ik het echt handig. Op school snap ik soms niet alles meteen. Met WisWiz kan ik dan even rustig de uitleg teruglezen."
  },
  {
    type: "student",
    name: "Linda Hendriks",
    klas: "3e klas VWO",
    avatar: "/linda-hendriks.png",
    quote:
      "Wiswiz maakt wiskunde een stuk makkelijker. De uitleg is duidelijk, simpel en to the point, waardoor je zelfs moeilijke sommen snel snapt. Super fijn om mee te oefenen en echt een boost voor je cijfers!"
  }
];

function ReviewCard({ tile }: { tile: Tile }) {
  const content = (
    <div className={cn(
      "bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col h-full",
      tile.link && "hover:border-info/40 hover:shadow-md transition-all duration-300 group"
    )}>
      <p className="text-sm text-gray-800 leading-relaxed flex-1">
        {tile.noQuotes ? tile.quote : `"${tile.quote}"`}
      </p>
      {tile.link && (
        <span className="text-[11px] text-info font-semibold group-hover:underline mt-2">
          {tile.linkLabel ?? `Lees het artikel in ${tile.name}`}
        </span>
      )}
      {tile.logo ? (
        <div className="flex flex-col items-center gap-2 mt-4 pt-3 border-t border-gray-50 w-full">
          <img
            src={tile.logo}
            alt={tile.name}
            className={`h-16 max-w-[140px] w-auto object-contain ${tile.logoClass ?? ""}`}
          />
        </div>
      ) : (
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-50">
          {tile.avatar && (
            <img
              src={tile.avatar}
              alt={tile.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <div>
            <p className="text-sm font-semibold text-gray-900">{tile.name}</p>
            {tile.subtitle && <p className="text-xs text-gray-800">{tile.subtitle}</p>}
            {tile.klas && <p className="text-xs text-gray-800">{tile.klas}</p>}
          </div>
        </div>
      )}
    </div>
  );

  if (tile.link) {
    return (
      <a href={tile.link} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return content;
}

function MobileOrgStrip() {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-xs text-gray-400 mb-1">Tik op een logo voor meer info</p>
      <div className="flex justify-center gap-2 overflow-x-auto">
        {ORG_TILES.map((tile, i) => (
          <button
            key={i}
            onClick={() => setExpanded(expanded === i ? null : i)}
            className={`bg-white rounded-xl shadow-sm border p-2.5 flex items-center justify-center w-14 h-14 flex-shrink-0 transition-colors cursor-pointer ${expanded === i ? "border-info/40" : "border-gray-100"}`}
          >
            <img
              src={tile.logo}
              alt={tile.name}
              className={`h-9 max-w-[48px] w-auto object-contain ${tile.logoClass ?? ""}`}
            />
          </button>
        ))}
      </div>
      {expanded !== null && (
        <ReviewCard tile={ORG_TILES[expanded]} />
      )}
    </div>
  );
}

function MobileStudentCard({ tile }: { tile: Tile }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-b-0">
      {tile.avatar && (
        <img
          src={tile.avatar}
          alt={tile.name}
          className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
        />
      )}
      <div className="min-w-0">
        <p className="text-xs text-gray-800 leading-relaxed">"{tile.quote}"</p>
        <p className="text-[11px] font-semibold text-gray-500 mt-1">
          {tile.name}{tile.klas ? ` · ${tile.klas}` : ""}{tile.subtitle ? ` · ${tile.subtitle}` : ""}
        </p>
      </div>
    </div>
  );
}

export default function StudentReviews() {
  const [showStudents, setShowStudents] = useState(false);

  return (
    <section className="py-8">
      <Container>
        <h2 className="text-lg md:text-3xl font-bold text-gray-900 text-center mb-4 md:mb-8">
          Instituten en leerlingen over WisWiz
        </h2>

        {/* Desktop: card grid */}
        <div className="hidden md:flex md:flex-wrap md:justify-center lg:grid lg:grid-cols-5 gap-4">
          {ORG_TILES.map((tile, i) => (
            <div key={i} className="md:w-[calc(33.333%-11px)] lg:w-auto">
              <ReviewCard tile={tile} />
            </div>
          ))}
        </div>

        {/* Mobile: logo strip */}
        <div className="md:hidden">
          <MobileOrgStrip />
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => setShowStudents(!showStudents)}
            className="border-2 border-info text-info font-bold text-sm py-2.5 px-5 rounded-lg hover:bg-info/10 transition-colors cursor-pointer"
          >
            {showStudents ? "Verberg leerlingenreviews" : "Bekijk wat leerlingen zeggen"}
          </button>
        </div>

        {showStudents && (
          <>
            {/* Desktop: card grid */}
            <div className="hidden md:flex md:flex-wrap md:justify-center lg:grid lg:grid-cols-5 gap-4 mt-4">
              {STUDENT_TILES.map((tile, i) => (
                <div key={i} className="md:w-[calc(33.333%-11px)] lg:w-auto">
                  <ReviewCard tile={tile} />
                </div>
              ))}
            </div>

            {/* Mobile: compact list */}
            <div className="md:hidden bg-white rounded-xl shadow-sm border border-gray-100 p-4 mt-4">
              {STUDENT_TILES.map((tile, i) => (
                <MobileStudentCard key={i} tile={tile} />
              ))}
            </div>
          </>
        )}
      </Container>
    </section>
  );
}
